import axios from 'axios';
import { getAuth } from 'firebase/auth';
import firebaseApp from '../firebase/config'; // Assuming this is your firebase init file

const API_URL = process.env.REACT_APP_API_URL;
console.log('[contentService] Using API URL:', API_URL);

const auth = getAuth(firebaseApp);

// Helper to get the current user's Firebase ID token
const getAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken(true); // true forces token refresh
      return token;
    } catch (error) {
      console.error('[contentService] Error getting auth token:', error);
      if (error.code === 'auth/user-token-expired' || error.code === 'auth/invalid-user-token') {
        console.warn('[contentService] User token expired or invalid. User may need to re-authenticate.');
      }
      return null;
    }
  }
  console.warn('[contentService] No current user found for getting auth token.');
  return null;
};

// Map admin section IDs to backend API paths
const sectionIdToPathMap = {
  heroSection: 'hero',
  aboutUsSection: 'aboutus',
  servicesSection: 'services',
  portfolioSection: 'portfolio', // Note: Portfolio might be more complex than a single path
  generalSection: 'general',
  contactUsSection: 'contactus', // Added for Contact Us section
  navbarSection: 'navbar',
  // contactPageSection: 'contactpage', // Example if you add contact page editor
  // navigationSection: 'navigation' // Example for nav editor
};

// Generic function to get content for a section
export const getSectionContent = async (sectionId) => {
  const pathSegment = sectionIdToPathMap[sectionId];
  if (!pathSegment) {
    console.error(`[contentService] No API path configured for sectionId: ${sectionId}`);
    throw new Error(`No API path configured for sectionId: ${sectionId}`);
  }
  const url = `${API_URL}/${pathSegment}`;
  console.log(`[contentService] Getting ${sectionId} from ${url}`);

  try {
    const token = await getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn(`[contentService] No auth token available for getSectionContent on ${sectionId}. Proceeding without token if endpoint is public.`);
    }

    const response = await axios.get(url, { headers });
    console.log(`[contentService] Successfully fetched ${sectionId} data:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error getting ${sectionId} via API:`, error.message);
    if (error.response) {
      console.error('[contentService] Error response data:', error.response.data);
      console.error('[contentService] Error response status:', error.response.status);
    }
    throw error;
  }
};

// Generic function to update content for a section
export const updateSectionContent = async (sectionId, data) => {
  const pathSegment = sectionIdToPathMap[sectionId];
  if (!pathSegment) {
    console.error(`[contentService] No API path configured for sectionId: ${sectionId}`);
    throw new Error(`No API path configured for sectionId: ${sectionId}`);
  }
  const url = `${API_URL}/${pathSegment}`;
  console.log(`[contentService] Updating ${sectionId} at ${url} with data:`, data);

  try {
    const token = await getAuthToken();
    if (!token) {
      console.error(`[contentService] No auth token available for updateSectionContent on ${sectionId}. Update failed.`);
      throw new Error('Authentication required to update content.');
    }

    const response = await axios.put(url, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    console.log(`[contentService] Successfully updated ${sectionId}:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error updating ${sectionId} via API:`, error.message);
     if (error.response) {
      console.error('[contentService] Error response data:', error.response.data);
      console.error('[contentService] Error response status:', error.response.status);
    }
    throw error;
  }
};

// --- Brands ---
export const getBrands = async () => {
  const url = `${API_URL}/brands`;
  try {
    const token = await getAuthToken();
    const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error getting brands:', error.message);
    throw error;
  }
};

export const createBrand = async (brandData) => {
  const url = `${API_URL}/brands`;
  try {
    const token = await getAuthToken();
    const response = await axios.post(url, brandData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error creating brand:', error.message);
    throw error;
  }
};

export const updateBrand = async (brandId, brandData) => {
  const url = `${API_URL}/brands/${brandId}`;
  try {
    const token = await getAuthToken();
    const response = await axios.put(url, brandData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error updating brand ${brandId}:`, error.message);
    throw error;
  }
};

export const deleteBrand = async (brandId) => {
  const url = `${API_URL}/brands/${brandId}`;
  try {
    const token = await getAuthToken();
    await axios.delete(url, { headers: { 'Authorization': `Bearer ${token}` } });
  } catch (error) {
    console.error(`[contentService] Error deleting brand ${brandId}:`, error.message);
    throw error;
  }
};

// --- Categories ---
export const getCategories = async () => {
  const url = `${API_URL}/categories`;
  try {
    const token = await getAuthToken();
    const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error getting categories:', error.message);
    throw error;
  }
};

export const createCategory = async (categoryData) => {
  const url = `${API_URL}/categories`;
  try {
    const token = await getAuthToken();
    const response = await axios.post(url, categoryData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error creating category:', error.message);
    throw error;
  }
};

export const updateCategory = async (categoryId, categoryData) => {
  const url = `${API_URL}/categories/${categoryId}`;
  try {
    const token = await getAuthToken();
    const response = await axios.put(url, categoryData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error updating category ${categoryId}:`, error.message);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  const url = `${API_URL}/categories/${categoryId}`;
  try {
    const token = await getAuthToken();
    await axios.delete(url, { headers: { 'Authorization': `Bearer ${token}` } });
  } catch (error) {
    console.error(`[contentService] Error deleting category ${categoryId}:`, error.message);
    throw error;
  }
};

// --- Products ---
export const getProducts = async (params = {}) => {
  const url = `${API_URL}/products`;
  try {
    const token = await getAuthToken();
    const response = await axios.get(url, { params, headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error getting products:', error.message);
    throw error;
  }
};

export const getProductById = async (productId) => {
  const url = `${API_URL}/products/${productId}`;
  try {
    const token = await getAuthToken();
    const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error getting product ${productId}:`, error.message);
    throw error;
  }
};

export const createProduct = async (productData) => {
  const url = `${API_URL}/products`;
  const isFormData = productData instanceof FormData;
  try {
    const token = await getAuthToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await axios.post(url, productData, { headers });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error creating product:', error.message);
    throw error;
  }
};

export const updateProduct = async (productId, productData) => {
  const url = `${API_URL}/products/${productId}`;
  const isFormData = productData instanceof FormData;
  try {
    const token = await getAuthToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await axios.put(url, productData, { headers });
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error updating product ${productId}:`, error.message);
    throw error;
  }
};

export const deleteProduct = async (productId) => {
  const url = `${API_URL}/products/${productId}`;
  try {
    const token = await getAuthToken();
    await axios.delete(url, { headers: { 'Authorization': `Bearer ${token}` } });
  } catch (error) {
    console.error(`[contentService] Error deleting product ${productId}:`, error.message);
    throw error;
  }
};

// --- Dealer Logos ---
export const getDealerLogos = async () => {
  const url = `${API_URL}/dealerlogos`;
  try {
    const token = await getAuthToken();
    const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error getting dealer logos:', error.message);
    throw error;
  }
};

export const addDealerLogo = async (logoData) => { // logoData should be FormData
  const url = `${API_URL}/dealerlogos`;
  try {
    const token = await getAuthToken();
    const response = await axios.post(url, logoData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error('[contentService] Error adding dealer logo:', error.message);
    throw error;
  }
};

export const updateDealerLogo = async (logoId, logoData) => { // logoData should be FormData
  console.log(`[contentService] updateDealerLogo function called for ID: ${logoId}.`);
  // Log FormData content
  if (logoData instanceof FormData) {
    console.log('[contentService] FormData entries:');
    for (let pair of logoData.entries()) {
      console.log(`  ${pair[0]}: ${pair[1]}`);
    }
  } else {
    console.log('[contentService] logoData is not FormData:', logoData);
  }
  const url = `${API_URL}/dealerlogos/${logoId}`;
  try {
    const token = await getAuthToken();
    const response = await axios.put(url, logoData, { headers: { 'Authorization': `Bearer ${token}` } });
    return response.data;
  } catch (error) {
    console.error(`[contentService] Error updating dealer logo ${logoId}:`, error.message);
    throw error;
  }
};

export const deleteDealerLogo = async (logoId) => {
  const url = `${API_URL}/dealerlogos/${logoId}`;
  try {
    const token = await getAuthToken();
    await axios.delete(url, { headers: { 'Authorization': `Bearer ${token}` } });
  } catch (error) {
    console.error(`[contentService] Error deleting dealer logo ${logoId}:`, error.message);
    throw error;
  }
};

// --- Image Upload (Generic) ---
export const uploadImage = async (fileData) => { // fileData should be FormData
  const url = `${API_URL}/upload`;
  try {
    const token = await getAuthToken();
    const response = await axios.post(url, fileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    console.log('[contentService] Image uploaded successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('[contentService] Error uploading image:', error.message);
    if (error.response) {
      console.error('[contentService] Upload error response data:', error.response.data);
    }
    throw error;
  }
};